import { renderPage } from './render';

// A4 i millimeter
const MARGIN = 18;
const INK = [34, 32, 28];
const MUTED = [92, 85, 74];
const ACCENT = [122, 75, 18];

const nextFrame = () => new Promise((resolve) => setTimeout(resolve, 0));

export const exportPdf = async ({ book, actions, answers, name, onProgress }) => {
  // jsPDF er stort, så det lastes først når noen faktisk eksporterer
  const { default: jsPDF } = await import('jspdf');
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const contentWidth = pageWidth - MARGIN * 2;
  const bottom = pageHeight - MARGIN;
  const studentName = (name || '').trim();
  const total = book.pages.length;
  const allPages = [{ imageUrl: book.cover.imageUrl }, ...book.pages];

  const header = (label) => {
    pdf.setFont('helvetica', 'normal').setFontSize(9).setTextColor(...MUTED);
    pdf.text(studentName ? `${book.title} · ${studentName}` : book.title, MARGIN, 12);
    pdf.text(label, pageWidth - MARGIN, 12, { align: 'right' });
    pdf.setDrawColor(216, 207, 190).setLineWidth(0.3).line(MARGIN, 15, pageWidth - MARGIN, 15);
  };

  const addImage = async (imageUrl, pageActions, maxHeight, top) => {
    const canvas = await renderPage(imageUrl, pageActions);
    const aspect = canvas.width / canvas.height;
    let height = maxHeight;
    let width = height * aspect;
    if (width > contentWidth) { width = contentWidth; height = width / aspect; }
    // JPEG gir en mye mindre PDF enn PNG, og bruker mindre minne underveis
    pdf.addImage(canvas.toDataURL('image/jpeg', 0.9), 'JPEG', (pageWidth - width) / 2, top, width, height);
    canvas.width = 0;
    return top + height;
  };

  for (let i = 0; i < allPages.length; i++) {
    onProgress?.(i + 1, allPages.length);
    await nextFrame();
    if (i > 0) pdf.addPage();
    const pageActions = actions[`page_${i}`] || [];

    if (i === 0) {
      let y = await addImage(book.cover.imageUrl, pageActions, 190, MARGIN + 4);
      pdf.setFont('times', 'bold').setFontSize(26).setTextColor(...INK);
      pdf.text(book.title, pageWidth / 2, y + 16, { align: 'center' });
      if (studentName) {
        pdf.setFont('helvetica', 'normal').setFontSize(13).setTextColor(...MUTED);
        pdf.text(`Laget av ${studentName}`, pageWidth / 2, y + 26, { align: 'center' });
      }
      continue;
    }

    const page = allPages[i];
    const pageLabel = `Side ${i} av ${total}`;
    header(pageLabel);
    let y = await addImage(page.imageUrl, pageActions, 118, 21) + 9;

    // Skriver tekst linje for linje og fortsetter på et nytt ark ved behov
    const write = (text, { font = 'times', style = 'normal', size = 11.5, color = INK, lineHeight = 5.2, gapAfter = 0 } = {}) => {
      pdf.setFont(font, style).setFontSize(size).setTextColor(...color);
      for (const line of pdf.splitTextToSize(text, contentWidth)) {
        if (y > bottom) {
          pdf.addPage();
          header(`${pageLabel} (forts.)`);
          y = 24;
          pdf.setFont(font, style).setFontSize(size).setTextColor(...color);
        }
        pdf.text(line, MARGIN, y);
        y += lineHeight;
      }
      y += gapAfter;
    };

    const pageAnswers = answers[`page_${i}`] || [];
    if (page.mode === 'referat') {
      write('Referat', { font: 'helvetica', style: 'bold', size: 12, lineHeight: 7 });
      const text = (pageAnswers[0] || '').trim();
      write(text || '(ikke skrevet)', { size: 12, lineHeight: 5.6, color: text ? INK : MUTED, style: text ? 'normal' : 'italic' });
    } else {
      if (page.label) write(page.label, { font: 'helvetica', style: 'bold', size: 8, color: ACCENT, lineHeight: 5.5 });
      write(page.title, { style: 'bold', size: 16, lineHeight: 7.5 });
      write(page.text, { gapAfter: 4 });
      page.questions.forEach((question, q) => {
        write(`${q + 1}. ${question}`, { font: 'helvetica', style: 'bold', size: 10.5, lineHeight: 5, gapAfter: 1 });
        const answer = (pageAnswers[q] || '').trim();
        write(answer || '(ikke besvart)', { color: answer ? INK : MUTED, style: answer ? 'normal' : 'italic', gapAfter: 4 });
      });
    }
  }

  pdf.save(book.pdfFileName);
};
